<script lang="ts">
  /**
   * One report, with the two Waze buttons under it (brief §9).
   *
   * Whether the buttons are offered is decided here, in the browser, by the *same*
   * `canConfirm` the server uses — that is what `src/domain` is for. The server checks again
   * and is the authority; this copy exists so the refusal is a greyed-out button with a
   * reason next to it instead of a round trip that ends in an error.
   */
  import type { Attribute } from '$domain/attributes.ts';
  import { canConfirm, REFUSAL_MESSAGES, type Verdict } from '$domain/confirm.ts';
  import { cellFor } from '$domain/h3.ts';
  import { request } from './api.ts';
  import { outbox } from './outbox.svelte.ts';
  import { describeError } from './session.svelte.ts';
  import type { Sighting } from './types.ts';

  type Props = {
    sighting: Sighting;
    attributes: Attribute[];
    /** The viewer's own id, for the author check. */
    viewerId: string;
    onclose: () => void;
    onvoted: (tally: { confirmed: number; disputed: number; hiddenNow: boolean }) => void;
  };

  const { sighting, attributes, viewerId, onclose, onvoted }: Props = $props();

  const CROWD_LABELS: Record<number, string> = { 1: 'einzeln', 2: 'ein paar', 3: 'viele' };

  let position = $state<GeolocationPosition | undefined>();
  let locating = $state(true);
  let busy = $state(false);
  let error = $state('');
  /**
   * What this session voted, if anything — laid *over* the numbers that came with the report.
   *
   * Deriving rather than copying matters twice: the buttons update the instant the server
   * answers instead of waiting for the map to reload, and opening a different report without
   * closing the sheet first cannot leave the previous one's tally on screen, which a plain
   * `$state` initialised from the prop would.
   */
  let cast = $state<{ confirmed: number; disputed: number; verdict: Verdict } | undefined>();
  let reporting = $state(false);
  let reported = $state('');
  const tally = $derived(cast ?? { confirmed: sighting.confirmed, disputed: sighting.disputed });
  const myVerdict = $derived<Verdict | null>(cast?.verdict ?? sighting.myVerdict);

  /**
   * The author id is not in the payload — only `mine` is — so the check is fed the viewer's
   * own id when it is their report and a value that cannot collide otherwise. Shipping the
   * real author id would name somebody in a response that does not otherwise identify them.
   */
  const decision = $derived.by(() => {
    if (position === undefined) return undefined;
    return canConfirm(
      {
        authorId: sighting.mine ? viewerId : `not-${viewerId}`,
        cell: sighting.cell,
        observedAt: new Date(sighting.observedAt),
        hidden: sighting.hidden,
      },
      viewerId,
      cellFor(position.coords.latitude, position.coords.longitude),
      new Date(),
    );
  });

  const rated = $derived(
    attributes
      .filter((attribute) => sighting.ratings[attribute.key] !== undefined)
      .map((attribute) => ({ attribute, value: sighting.ratings[attribute.key] ?? 0 })),
  );

  const age = $derived.by(() => {
    const minutes = Math.round((Date.now() - Date.parse(sighting.observedAt)) / 60_000);
    if (minutes < 1) return 'gerade eben';
    if (minutes < 60) return `vor ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `vor ${hours} h`;
    return `vor ${Math.round(hours / 24)} Tagen`;
  });

  $effect(() => {
    // A coarse fix is fine here: the question is which of seven hexagons you are standing in,
    // not where exactly, so waiting for a high-accuracy lock would only make the sheet slower.
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        position = fix;
        locating = false;
      },
      () => {
        locating = false;
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  });

  async function vote(verdict: Verdict) {
    if (position === undefined) return;
    busy = true;
    error = '';
    try {
      await outbox.enqueue('confirm', {
        sightingId: sighting.id,
        verdict,
        lat: position.coords.latitude,
        lon: position.coords.longitude,
      });

      // Optimistic, because no answer is coming back from this call (brief §14a.3). The
      // arithmetic is the obvious one: this vote replaces whatever was cast before it.
      const previous = myVerdict;
      const confirmed = tally.confirmed + (verdict === 1 ? 1 : 0) - (previous === 1 ? 1 : 0);
      const disputed = tally.disputed + (verdict === -1 ? 1 : 0) - (previous === -1 ? 1 : 0);
      cast = { confirmed, disputed, verdict };
      onvoted({ confirmed, disputed, hiddenNow: false });
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }
</script>

<div class="backdrop" role="presentation" onclick={onclose}></div>

<section class="sheet" aria-label="Meldung">
  <header>
    <div>
      <h2>{CROWD_LABELS[sighting.crowd] ?? '?'} · {age}</h2>
      <p class="by">von @{sighting.username}{sighting.mine ? ' (du)' : ''}</p>
    </div>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  {#if sighting.hidden}
    <p class="hidden-note" role="status">
      Ausgeblendet{sighting.hiddenReason === 'disputed' ? ', weil mehrere widersprochen haben' : ''}.
      Nur du siehst sie noch.
    </p>
  {/if}

  {#if sighting.note}
    <p class="note">„{sighting.note}"</p>
  {/if}

  <ul class="ratings">
    {#each rated as entry (entry.attribute.key)}
      <li>
        <span>{entry.attribute.emoji} {entry.attribute.label}</span>
        {#if entry.attribute.kind === 'stars'}
          <span class="stars" aria-label={`${entry.value} von 5`}>
            {'★'.repeat(entry.value)}<span class="dim">{'★'.repeat(5 - entry.value)}</span>
          </span>
        {:else}
          <span class="scale-value">
            {entry.value <= 2 ? entry.attribute.ends?.[0] : entry.attribute.ends?.[1]}
          </span>
        {/if}
      </li>
    {/each}
  </ul>

  <p class="tally">
    <span class="up">👍 {tally.confirmed}</span>
    <span class="down">👎 {tally.disputed}</span>
  </p>

  {#if error}<p class="error" role="alert">{error}</p>{/if}

  {#if reported}
    <p class="hint">{reported}</p>
  {:else if reporting}
    <!-- Brief §18. The reason is required because "melden" without one is a downvote, and the
         admin queue would then be a list of things somebody disliked. -->
    <form
      class="report"
      onsubmit={async (event) => {
        event.preventDefault();
        const reason = new FormData(event.currentTarget).get('reason');
        if (typeof reason !== 'string' || reason.trim() === '') return;
        try {
          await request(`/api/sightings/${sighting.id}/report`, {
            method: 'POST',
            body: JSON.stringify({ reason: reason.trim() }),
          });
          reported = 'Danke — ein Admin sieht sich das an.';
        } catch (cause) {
          error = describeError(cause);
        }
      }}
    >
      <input name="reason" placeholder="Was stimmt daran nicht?" maxlength="200" required />
      <button type="submit">Melden</button>
    </form>
  {:else if !sighting.mine}
    <button class="report-open" onclick={() => (reporting = true)}>Melden</button>
  {/if}

  {#if locating}
    <p class="hint">Position wird bestimmt …</p>
  {:else if position === undefined}
    <p class="hint">Ohne Standortfreigabe lässt sich nicht bestätigen.</p>
  {:else if decision !== undefined && !decision.ok}
    <p class="hint">{REFUSAL_MESSAGES[decision.reason]}</p>
  {:else}
    <div class="verdicts">
      <button
        class="verdict up"
        class:chosen={myVerdict === 1}
        disabled={busy}
        onclick={() => vote(1)}
      >
        👍 Stimmt
      </button>
      <button
        class="verdict down"
        class:chosen={myVerdict === -1}
        disabled={busy}
        onclick={() => vote(-1)}
      >
        👎 Stimmt nicht
      </button>
    </div>
    {#if myVerdict !== null}
      <p class="hint">Deine Antwort ist gespeichert — du kannst sie noch ändern.</p>
    {/if}
  {/if}
</section>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
    z-index: 5;
  }

  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-height: 80vh;
    overflow-y: auto;
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
    border-radius: 1.25rem 1.25rem 0 0;
    background: var(--card);
    box-shadow: 0 -8px 32px rgb(0 0 0 / 25%);
  }

  header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .by,
  .hint,
  .error,
  .hidden-note {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .error {
    color: var(--accent);
  }

  .hidden-note {
    padding: 0.5rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
  }

  .note {
    margin: 0;
    font-size: 0.9375rem;
    font-style: italic;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .ratings {
    display: grid;
    gap: 0.3rem;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 0.9375rem;
  }

  .ratings li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .stars {
    color: #f5a524;
    letter-spacing: 0.05em;
  }

  .dim {
    color: var(--border);
  }

  .scale-value {
    color: var(--muted);
  }

  .tally {
    display: flex;
    gap: 1rem;
    margin: 0;
    font-size: 0.9375rem;
    color: var(--muted);
  }

  .report {
    display: flex;
    gap: 0.4rem;
  }

  .report input {
    flex: 1;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
  }

  .report button,
  .report-open {
    align-self: flex-start;
    padding: 0.5rem 0.8rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .verdicts {
    display: flex;
    gap: 0.5rem;
  }

  .verdict {
    flex: 1;
    padding: 0.85rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.9375rem;
    cursor: pointer;
  }

  .verdict.chosen {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 18%, transparent);
  }

  .verdict:disabled {
    opacity: 0.6;
    cursor: default;
  }
</style>
